import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Link } from 'react-router-dom';
import { ColDef } from 'ag-grid-community';
import { handleExportClick } from '../../service/csvExporter';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import edit from '../../assets/editOff.svg';
import Pagination from '../../pagination/Pagination';
import Spinner from '../../spinner/Spinner';
import mediaUrl from '../../service/mediaUrl';
import { useSelector } from 'react-redux';
import searchIcon from '../../assets/search.svg';
import downloadIcon from '../../assets/download.svg';
import { statusCellRenderer } from '../../statusCellRenderer/statusCelleRenderer';
import { AddPublicationButton } from '../../addPublicationButton/AddPublicationButton';
import { socialMediaCellRenderer } from '../../socialMediaCellRenderer/socialMediaCellRenderer';
import { PageLayout } from '../../layout/PageLayout';
import { PublicationPageHeader } from '../PublicationPageHeader';
import { ButtonLink } from '../../components/Button';
import { selectIsSsoUser, selectUserId } from '../../selectors/userSelectors';

interface Publication {
    media: string;
    extension: string;
    title: string;
    content: string;
    status: string;
    createdAt: string;
}

const search = { path: searchIcon };
const download = { path: downloadIcon };

const publicationCard = (params: any) => {
    return (
        <>
            <div className={'flex w-full items-center relative break-words'}>
                <img
                    src={mediaUrl(params.data)}
                    className={'w-[80px] h-[80px] p-[10px] rounded-[15px] flex'}
                    alt="illustration"
                />
                <div className={'flex flex-col w-[75%] overflow-hidden'}>
                    <div className={'overflow-hidden text-ellipsis w-full'}>
                        <span
                            className={
                                'relative text-medium font-medium text-dark-gray my-[10px] w-full'
                            }
                        >
                            {params.data.title}
                        </span>
                    </div>
                    <div className={'overflow-hidden text-ellipsis w-full'}>
                        <span
                            className={
                                'relative text-medium font-small text-gray w-full whitespace-normal leading-[1.5em] line-clamp-2'
                            }
                        >
                            {params.data.content}
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

function ArchivedPublications() {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [publications, setPublications] = useState<Publication[]>([]);
    const userId = useSelector(selectUserId);
    const isSsoUser = useSelector(selectIsSsoUser);
    let itemsPerPage = 13;
    const url = `/api/publications`;

    async function fecthAllPublications(): Promise<Publication[]> {
        setLoading(true);
        let allPublications: Publication[] = [];
        let fetchPage = 1;
        let itemsPerPage = 500;
        let hasMore = true;

        while (hasMore) {
            const response = await axios.get(
                url + `?itemsPerPage=${itemsPerPage}&page=${fetchPage}&order%5BcreatedAt%5D=desc`,
            );
            allPublications = allPublications.concat(response.data['hydra:member']);
            const allItems = response.data['hydra:totalItems'];
            hasMore = fetchPage * itemsPerPage < allItems;
            fetchPage++;
            setCurrentPage(fetchPage);
        }
        setLoading(false);

        return allPublications;
    }

    const headers = [
        'id',
        'title',
        'content',
        'media',
        'link',
        'social',
        'status',
        'createdAt',
        'date',
    ];

    const onExportArchive = () => {
        handleExportClick(
            headers,
            fecthAllPublications,
            'archived.csv',
            () => setLoading(true),
            () => setLoading(false),
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (userId === undefined) {
                    return;
                }

                const userQuery = isSsoUser
                    ? `&createdByUser=${userId}`
                    : `&createdByAdmin=${userId}`;

                const response = await axios.get(
                    url +
                        `?status=0&page=${currentPage}&order%5BcreatedAt%5D=desc&itemsPerPage=${itemsPerPage}` +
                        userQuery,
                );

                const publications = response.data['hydra:member'];
                const totalItems = response.data['hydra:totalItems'];
                setTotalPage(Math.ceil(totalItems / itemsPerPage));
                setPublications(publications);
            } catch (error) {
                console.error('Erreur lors de la récupération des publications :', error);
            }
        };
        fetchData();
    }, [url, currentPage, itemsPerPage, isSsoUser, userId]);

    function createdAtFormatter(params: any) {
        let dateAsString = params.data.createdAt;
        let newDate = new Date(dateAsString);
        return newDate.toLocaleString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    }

    function actionButtons(params: any) {
        let id = params.data.id;
        return (
            <>
                <Link to={`/edit_publication/${id}`}>
                    <img src={edit} className={'w-[20px] h-[20px]'} alt="edit icon" />
                </Link>
            </>
        );
    }

    const columnDefs: ColDef<Publication>[] = [
        {
            headerName: 'Publication',
            cellRenderer: publicationCard,
            autoHeight: true,
            resizable: false,
            flex: 3,
        },
        {
            headerName: 'Status',
            cellRenderer: statusCellRenderer,
            resizable: false,
            flex: 1,
        },
        {
            headerName: 'Créée Le',
            field: 'createdAt',
            valueFormatter: createdAtFormatter,
            resizable: false,
            flex: 1,
        },
        {
            headerName: 'Réseaux Sociaux',
            cellRenderer: socialMediaCellRenderer,
            resizable: false,
        },
        {
            headerName: 'Action',
            cellRenderer: actionButtons,
            resizable: false,
            flex: 1,
        },
    ];

    const containerStyle = useMemo(
        () => ({
            width: '100%',
            height: '-webkit-fill-available',
            fontFamily: 'Montserrat',
        }),
        [],
    );

    return (
        <PageLayout>
            <PublicationPageHeader
                title="Mes publications archivées"
                actions={
                    <>
                        <ButtonLink to={'/publications'} isPrimary={false}>
                            Mes publications
                        </ButtonLink>
                        <AddPublicationButton />
                    </>
                }
            />
            <div className={'relative flex w-full justify-between pb-8'}>
                <div className="flex relative">
                    <input
                        type="text"
                        className="flex w-full min-w-80 max-w-80 text-small"
                        placeholder="Rechercher une publication..."
                    />
                    <img src={search.path} alt="search icon" className="-ml-7 mt-4 w-4 h-4" />
                </div>

                {!isSsoUser && (
                    <div className={'flex'}>
                        <button
                            className="flex items-center text-dark-gray text-medium font-medium bg-primary-light"
                            onClick={onExportArchive}
                            disabled={loading}
                        >
                            <img src={download.path} alt="download icon" className="mr-3 w-8 h-8" />
                            Exporter
                        </button>
                    </div>
                )}
            </div>
            <div className="ag-theme-alpine" style={containerStyle}>
                <Spinner loading={loading} />
                <AgGridReact
                    rowData={publications}
                    columnDefs={columnDefs}
                    domLayout="autoHeight"
                    pagination={false}
                    overlayNoRowsTemplate={'Pas de données enregistrées'}
                    suppressNoRowsOverlay={!!publications}
                />
            </div>
            <Pagination
                currentPage={currentPage}
                totalPage={totalPage}
                previous={() => setCurrentPage(p => p - 1)}
                next={() => setCurrentPage(p => p + 1)}
            />
        </PageLayout>
    );
}

export default ArchivedPublications;
